// Tiny fetch wrapper. In dev, point at localhost:8011; in prod, same-origin /api.
// Detected by hostname so the same build works in both contexts.
const isDev = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
const API_BASE = isDev ? 'http://127.0.0.1:8011/api' : '/api';
async function request(method, path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const err = await res.json();
            detail = err.detail ?? detail;
        }
        catch {
            // ignore
        }
        throw new ApiError(detail, res.status);
    }
    return res.json();
}
export class ApiError extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.status = status;
    }
    /** Server flagged this action as requiring a verified email. */
    get isVerifyEmail() {
        return this.status === 403 && this.message.startsWith('verify_email:');
    }
    /** Strip the sentinel prefix for display. */
    get cleanMessage() {
        return this.isVerifyEmail
            ? this.message.replace(/^verify_email:\s*/, '')
            : this.message;
    }
}
export const api = {
    signup: (email, password, display_name) => request('POST', '/auth/signup', { email, password, display_name }),
    anonymousSignup: (display_name) => request('POST', '/auth/anonymous', display_name ? { display_name } : {}),
    promoteGuest: (email, password, display_name) => request('POST', '/auth/promote-guest', { email, password, display_name }),
    login: (email, password) => request('POST', '/auth/login', { email, password }),
    twoFactorVerify: (challenge_token, code) => request('POST', '/auth/2fa/verify', { challenge_token, code }),
    twoFactorStatus: () => request('GET', '/auth/2fa/status'),
    twoFactorBeginEnroll: () => request('POST', '/auth/2fa/begin-enroll'),
    twoFactorConfirmEnroll: (code) => request('POST', '/auth/2fa/confirm-enroll', { code }),
    twoFactorDisable: (code) => request('POST', '/auth/2fa/disable', { code }),
    logout: () => request('POST', '/auth/logout'),
    me: () => request('GET', '/auth/me'),
    passwordResetRequest: (email) => request('POST', '/auth/password-reset/request', { email }),
    passwordResetConfirm: (token, new_password) => request('POST', '/auth/password-reset/confirm', { token, new_password }),
    verifyEmail: (token) => request('POST', '/auth/verify-email', { token }),
    resendVerification: () => request('POST', '/auth/verify-email/resend'),
    recentActivity: () => request('GET', '/auth/recent-activity'),
    changePassword: (old_password, new_password) => request('POST', '/auth/change-password', { old_password, new_password }),
    createMatch: () => request('POST', '/matches'),
    createPveMatch: (opts) => request('POST', '/matches/pve', opts ?? {}),
    progression: () => request('GET', '/progression'),
    acceptInvite: (code) => request('POST', '/matches/accept', { code }),
    listMatches: () => request('GET', '/matches'),
    getMatch: (id) => request('GET', `/matches/${id}`),
    submitTurn: (id, card_ids) => request('POST', `/matches/${id}/turn`, { card_ids }),
    stagePlank: (id, card_ids) => request('POST', `/matches/${id}/stage`, { card_ids }),
    shopAction: (id, action, offer_id) => request('POST', `/matches/${id}/shop`, { action, offer_id }),
    sellCrew: (id, crew_id) => request('POST', `/matches/${id}/sell`, { crew_id }),
    tossCard: (id, card_id) => request('POST', `/matches/${id}/toss`, { card_id }),
    refreshPort: (id) => request('POST', `/matches/${id}/port-refresh`),
    forfeitMatch: (id) => request('POST', `/matches/${id}/forfeit`),
    peekOppDraw: (id) => request('GET', `/matches/${id}/peek-opp-draw`),
    buryOppDraw: (id, card_id) => request('POST', `/matches/${id}/bury-opp-draw`, { card_id }),
    stats: () => request('GET', '/stats'),
    listFriends: () => request('GET', '/friends'),
    requestFriend: (name) => request('POST', '/friends/request', { name }),
    respondFriend: (request_id, accept) => request('POST', `/friends/${request_id}/respond`, { accept }),
    removeFriend: (other_user_id) => request('DELETE', `/friends/${other_user_id}`),
    challengeFriend: (friend_user_id) => request('POST', `/friends/${friend_user_id}/challenge`),
    acceptChallenge: (match_id) => request('POST', `/matches/${match_id}/accept-challenge`),
    declineChallenge: (match_id) => request('POST', `/matches/${match_id}/decline-challenge`),
    getProfile: () => request('GET', '/profile'),
    updateProfile: (body) => request('PUT', '/profile', body),
    telemetry: (event, payload, client_id) => request('POST', '/telemetry', { event, payload, client_id }),
    maintenanceStatus: () => request('GET', '/maintenance'),
    dailyChallenge: () => request('GET', '/daily-challenge'),
    dailyChallengeResult: (doubloons, ante_reached, won) => request('POST', '/daily-challenge/result', { doubloons, ante_reached, won }),
    collection: () => request('GET', '/collection'),
    challenges: () => request('GET', '/challenges'),
};
